import { EXPERIMENT_STEPS, fieldValueError, type ExperimentStepKey } from './schemas'

/**
 * Fields marked with an asterisk in the wizard. Keeping the list explicit (rather
 * than introspecting the zod schemas) means the UI, the completion percentage and
 * the submit gate all agree on exactly one definition of "required".
 */
export const REQUIRED_EXPERIMENT_FIELDS = [
  'title',
  'experimentDate',
  'researcherName',
  'texFiberId',
  'texSampleWeightDry',
  'morWaterTypeId',
  'morLiquorRatio',
  'morStartingPh',
  'morMordantIds',
  'dyeSourceIds',
  'dyeWaterTypeId',
  'dyeLiquorRatio',
  'dyeMaxTempC',
  'resLabL',
  'resLabA',
  'resLabB',
] as const

export type RequiredExperimentField = (typeof REQUIRED_EXPERIMENT_FIELDS)[number]

/** Every field the wizard can collect, grouped by the step that owns it. */
export const EXPERIMENT_FIELDS_BY_STEP = Object.fromEntries(
  EXPERIMENT_STEPS.filter((step) => step.schema !== null).map((step) => [
    step.key,
    Object.keys(step.schema!.shape),
  ]),
) as Record<Exclude<ExperimentStepKey, 'review'>, string[]>

function hasValue(value: unknown): boolean {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim() !== ''
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'number') return Number.isFinite(value)
  return true
}

export interface CompletionSummary {
  /** 0-100, across the required fields only. Drives the "68% complete" badge. */
  percent: number
  /** Required fields still empty. An experiment cannot leave draft while this is non-empty. */
  missingRequired: RequiredExperimentField[]
  /** Steps where at least one required field is still empty. */
  incompleteSteps: ExperimentStepKey[]
  canFinish: boolean
}

export function summariseCompletion(values: Record<string, unknown>): CompletionSummary {
  // A value that would be rejected, e.g. letters in a number field, does not count as filled.
  const missingRequired = REQUIRED_EXPERIMENT_FIELDS.filter(
    (field) => !hasValue(values[field]) || fieldValueError('experiment', field, values[field]) !== undefined,
  )
  const filled = REQUIRED_EXPERIMENT_FIELDS.length - missingRequired.length

  const incompleteSteps = (
    Object.entries(EXPERIMENT_FIELDS_BY_STEP) as [ExperimentStepKey, string[]][]
  )
    .filter(([, fields]) => fields.some((field) => missingRequired.includes(field as never)))
    .map(([step]) => step)

  return {
    percent: Math.round((filled / REQUIRED_EXPERIMENT_FIELDS.length) * 100),
    missingRequired: [...missingRequired],
    incompleteSteps,
    canFinish: missingRequired.length === 0,
  }
}
